<template>
  <div class="container py-4">
    <div class="row justify-content-center">
      <div class="col-md-8">
        <div class="page-bar py-3 mb-3">
          <div class="container">
            <div class="d-flex align-items-center">
              <BackButton class="btn btn-link p-0 mr-2" :fallback="fallbackPath">
                <i class="fas fa-chevron-left"></i>
              </BackButton>
              <div class="page-title flex-grow-1">Post to this event</div>
            </div>
          </div>
        </div>

        <section class="posts-shell">
          <div class="posts-header">
            <h1 class="posts-title mb-1">{{ eventTitle || 'Event posts' }}</h1>
            <p class="posts-subtitle mb-0">Updates, announcements, and posts for this event.</p>
          </div>

          <button type="button" class="composer-trigger" @click="openCreateModal">
            <img
              v-no-right-click
              :src="composerAvatar"
              alt="Your avatar"
              class="composer-trigger__avatar"
            />
            <span class="composer-trigger__input">Share an update for this event...</span>
          </button>

          <div class="filter-strip" role="tablist" aria-label="Post filters">
            <button
              v-for="filter in filters"
              :key="filter.valueKey"
              type="button"
              class="filter-chip"
              :class="{ active: activeFilter === filter.valueKey }"
              @click="activeFilter = filter.valueKey"
            >
              {{ filter.label }}
            </button>
          </div>

          <div v-if="loading" class="post-list">
            <article v-for="n in 3" :key="`skeleton-${n}`" class="post-card post-card--skeleton">
              <div class="post-head">
                <div class="post-user">
                  <div class="skeleton-avatar"></div>
                  <div class="w-100">
                    <div class="skeleton-line skeleton-line--name"></div>
                    <div class="skeleton-pill mt-2"></div>
                  </div>
                </div>
                <div class="skeleton-dots"></div>
              </div>
              <div class="skeleton-line skeleton-line--body mt-3"></div>
              <div class="skeleton-line skeleton-line--body"></div>
              <div class="skeleton-line skeleton-line--body short"></div>
            </article>
          </div>

          <div v-else-if="posts.length" ref="galleryHostRef" class="post-list">
            <article v-for="post in posts" :key="post.id" class="post-card">
              <div class="post-head">
                <div class="post-user">
                  <img
                    v-no-right-click
                    :src="post.userImage"
                    :alt="post.username"
                    class="post-avatar"
                  />
                  <div class="post-user-copy">
                    <div class="post-username">{{ post.username }}</div>
                    <div class="post-meta">
                      <i class="far fa-clock"></i>
                      <span>{{ post.createdAtLabel }}</span>
                    </div>
                    <div class="post-badges">
                      <span class="post-badge">{{ post.hostLabel }}</span>
                      <span v-if="post.typeLabel" class="post-badge post-badge--muted">{{ post.typeLabel }}</span>
                    </div>
                  </div>
                </div>
                <div class="post-menu-wrap">
                  <button
                    type="button"
                    class="post-menu"
                    aria-label="Post actions"
                    @click.stop="toggleMenu(post.id)"
                  >
                    <i class="fas fa-ellipsis-h"></i>
                  </button>
                  <div v-if="openMenuId === post.id" class="post-menu-dropdown">
                    <button type="button" class="post-menu-item" @click="handleEditPost(post)">
                      <i class="far fa-edit"></i>
                      <span>Edit</span>
                    </button>
                    <button type="button" class="post-menu-item post-menu-item--danger" @click="handleDeletePost(post)">
                      <i class="far fa-trash-alt"></i>
                      <span>Delete</span>
                    </button>
                  </div>
                </div>
              </div>

              <div v-if="post.kind === 'text_photo'" class="post-copy">
                <p v-if="post.title" class="post-text mb-0">{{ post.title }}</p>

                <div
                  v-if="post.images.length"
                  class="post-media-grid"
                  :class="[`count-${Math.min(post.images.length, 4)}`, { 'is-centered': post.images.length < 3 }]"
                >
                  <button
                    v-for="(image, imageIndex) in post.images.slice(0, 4)"
                    :key="`${post.id}-${imageIndex}`"
                    type="button"
                    class="post-media-tile"
                    :class="{ 'is-more': imageIndex === 3 && post.images.length > 4 }"
                    @click="openPostGallery(post, imageIndex)"
                  >
                    <img
                      v-no-right-click
                      :src="image"
                      :alt="`${post.username} post image ${imageIndex + 1}`"
                      class="post-media__img"
                    />
                    <span v-if="imageIndex === 3 && post.images.length > 4" class="post-media__more">
                      +{{ post.images.length - 4 }}
                    </span>
                  </button>
                </div>
              </div>

              <div v-else-if="post.kind === 'schedule'" class="post-panel">
                <p v-if="post.scheduleContext" class="post-panel__lead mb-0">{{ post.scheduleContext }}</p>
                <div class="post-schedule">
                  <div v-if="post.scheduleDetails" class="post-detail-row">
                    <i class="far fa-file-alt"></i>
                    <span>{{ post.scheduleDetails }}</span>
                  </div>
                  <div v-if="post.scheduleTime" class="post-detail-row">
                    <i class="far fa-clock"></i>
                    <span>{{ post.scheduleTime }}</span>
                  </div>
                  <div v-if="post.scheduleLocation" class="post-detail-row">
                    <i class="fas fa-map-marker-alt"></i>
                    <span>{{ post.scheduleLocation }}</span>
                  </div>
                </div>
              </div>

              <div v-else-if="post.kind === 'poll'" class="post-panel">
                <p v-if="post.pollContext" class="post-panel__lead mb-0">{{ post.pollContext }}</p>
                <p v-if="post.pollQuestion" class="post-poll-question mb-0">{{ post.pollQuestion }}</p>
                <div v-if="post.pollOptions.length" class="post-poll-options">
                  <button
                    v-for="(option, optionIndex) in post.pollOptions"
                    :key="`${post.id}-poll-${optionIndex}`"
                    type="button"
                    class="post-poll-option"
                    :class="{ 'is-selected': isPollOptionSelected(post, option), 'is-busy': busyPollPostId === post.id }"
                    :disabled="busyPollPostId === post.id"
                    @click="submitPollVote(post, option, optionIndex)"
                  >
                    <span>{{ option }}</span>
                    <span class="post-poll-option__count">{{ getPollOptionVoteCount(post, option) }}</span>
                  </button>
                </div>
              </div>

              <div v-else-if="post.kind === 'car_pool'" class="post-panel">
                <div class="post-chip-row">
                  <span v-if="post.poolDetails" class="post-badge post-badge--muted">{{ post.poolDetails }}</span>
                  <span v-if="post.poolSeats" class="post-badge post-badge--muted">{{ post.poolSeats }} Seats</span>
                </div>

                <div class="post-schedule car-pool-meta">
                  <div v-if="post.poolFrom" class="post-detail-row">
                    <i class="fas fa-map-pin"></i>
                    <span>From {{ post.poolFrom }}</span>
                  </div>
                  <div v-if="post.poolLeaving" class="post-detail-row">
                    <i class="fas fa-location-arrow"></i>
                    <span>To {{ post.poolLeaving }}</span>
                  </div>
                </div>
              </div>

              <p v-else-if="post.title" class="post-text mb-0">{{ post.title }}</p>

              <div class="post-footer">
                <a
                  v-if="post.kind === 'car_pool' && post.phoneNumber"
                  class="post-stat post-stat-btn post-stat-link"
                  :href="`tel:${post.phoneNumber}`"
                >
                  <i class="fas fa-phone-alt"></i>
                  <span>call</span>
                </a>
                <button
                  v-else
                  type="button"
                  class="post-stat post-stat-btn"
                  :disabled="busyPostId === post.id"
                  @click="toggleLike(post)"
                >
                  <span class="post-stat__emoji" :class="post.likeStatus ? 'is-liked' : ''">{{ post.likeStatus ? '❤️' : '🤍' }}</span>
                  <span>{{ post.likesCount }}</span>
                </button>
                <button type="button" class="post-stat post-stat-btn" @click="openComments(post)">
                  <span class="post-stat__emoji">💬</span>
                  <span>{{ commentsLabel(post.commentsCount) }}</span>
                </button>
                <button
                  v-if="post.kind === 'poll'"
                  type="button"
                  class="poll-view-btn"
                  @click="openPollModal(post)"
                >
                  View Poll
                </button>
              </div>
            </article>
          </div>

          <div v-else class="empty-posts">
            <div class="empty-posts__title">No posts yet</div>
            <p class="empty-posts__text mb-0">There are no posts for this filter right now.</p>
          </div>
        </section>
      </div>
    </div>
  </div>

  <div v-if="showCreateModal" class="composer-modal-backdrop" @click.self="closeCreateModal">
    <div class="composer-modal create-post-modal" role="dialog" aria-modal="true" aria-labelledby="createPostTitle">
      <div class="composer-modal__head">
        <div>
          <h2 id="createPostTitle" class="composer-modal__title mb-0">Create Post</h2>
          <div class="create-post-modal__subhead">Share a polished update with your event guests.</div>
        </div>
        <button type="button" class="composer-modal__close" aria-label="Close" @click="closeCreateModal">✕</button>
      </div>

      <div class="composer-modal__body create-post-modal__body">
        <section class="create-post-hero">
          <div class="composer-modal__user">
            <img
              v-no-right-click
              :src="composerAvatar"
              alt="Your avatar"
              class="composer-modal__avatar"
            />
            <div class="create-post-modal__identity">
              <div class="composer-modal__name">{{ composerName }}</div>
              <div v-if="composerHandle" class="composer-modal__tag">@{{ composerHandle }}</div>
              <div class="create-post-modal__audience">
                <label class="audience-pill" :class="{ active: composerAudience === 'everyone' }">
                  <input v-model="composerAudience" type="radio" value="everyone" />
                  <span>🌍 Everyone</span>
                </label>
                <label class="audience-pill" :class="{ active: composerAudience === 'only_me' }">
                  <input v-model="composerAudience" type="radio" value="only_me" />
                  <span>🔒 Only me</span>
                </label>
              </div>
            </div>
          </div>

          <div class="create-post-chip-group">
            <div class="create-post-chip-group__label">Post Type</div>
            <div class="create-post-tabs" role="tablist" aria-label="Post type">
              <button
                v-for="item in availableComposerTabs"
                :key="item.key"
                type="button"
                class="create-post-tab"
                :class="{ active: composerType === item.key }"
                @click="composerType = item.key"
              >
                <span class="create-post-tab__icon">{{ item.icon }}</span>
                <span>{{ item.label }}</span>
              </button>
            </div>
          </div>
        </section>

        <section class="create-post-compose">
          <div class="create-post-section create-post-section--main">
            <div class="create-post-section__title">Caption</div>
            <textarea
              v-model.trim="draftPostText"
              class="composer-modal__textarea create-post-modal__textarea"
              rows="6"
              placeholder="Share an update for this event..."
            ></textarea>

            <div v-if="composerType === 'textPhoto'" class="composer-filepond">
              <FilePondComponent
                ref="composerPond"
                name="composerImages"
                :allow-multiple="true"
                :max-files="3"
                :files="composerFiles"
                :accepted-file-types="composerAcceptedFileTypes"
                :label-idle="composerLabelIdle"
                :before-add-file="beforeAddComposerFile"
                @updatefiles="handleComposerFiles"
              />
              <div class="composer-filepond__hint">Up to 3 images. Max 5 MB each.</div>
            </div>
          </div>

          <div v-if="composerType === 'schedule'" class="create-post-section create-post-section--details">
            <div class="create-post-section__title">Schedule Details</div>
            <div class="create-post-fields-grid">
              <input v-model.trim="composerForm.scheduleTitle" type="text" class="composer-input" placeholder="Title" />
              <input v-model.trim="composerForm.scheduleStart" type="text" class="composer-input" placeholder="Start Time" />
              <input v-model.trim="composerForm.scheduleEnd" type="text" class="composer-input" placeholder="End Time" />
              <input v-model.trim="composerForm.scheduleLocation" type="text" class="composer-input composer-input--full" placeholder="Location" />
            </div>
          </div>

          <div v-else-if="composerType === 'carPool'" class="create-post-section create-post-section--details">
            <div class="create-post-section__title">Car Pool Details</div>
            <div class="create-post-fields-grid">
              <select v-model="composerForm.carPoolType" class="composer-input composer-input--full">
                <option disabled value="">Select Type</option>
                <option value="Request Ride">Request Ride</option>
                <option value="Offer Ride">Offer Ride</option>
              </select>
              <input v-model.trim="composerForm.carPoolFrom" type="text" class="composer-input" placeholder="From (optional)" />
              <input v-model.trim="composerForm.carPoolLeaving" type="text" class="composer-input" placeholder="Leaving around (optional)" />
              <input v-model.trim="composerForm.carPoolSeats" type="number" min="0" class="composer-input" placeholder="Seats (optional)" />
              <input v-model.trim="composerForm.carPoolPhone" type="tel" class="composer-input" placeholder="Phone Number" />
            </div>
          </div>

          <div v-else-if="composerType === 'poll'" class="create-post-section create-post-section--details">
            <div class="create-post-section__title">Poll Options</div>
            <div class="create-post-fields-grid">
              <input v-model.trim="composerForm.pollQuestion" type="text" class="composer-input composer-input--full" placeholder="Poll Question" />
              <div
                v-for="(option, index) in composerForm.pollOptions"
                :key="`poll-opt-${index}`"
                class="poll-option-field composer-input--full"
              >
                <input
                  v-model.trim="composerForm.pollOptions[index]"
                  type="text"
                  class="composer-input"
                  :placeholder="`Option ${index + 1}`"
                />
                <button
                  v-if="index >= 2"
                  type="button"
                  class="poll-option-remove"
                  :aria-label="`Remove option ${index + 1}`"
                  @click="removePollOption(index)"
                >
                  ✕
                </button>
              </div>
            </div>
            <button
              v-if="composerForm.pollOptions.length < 4"
              type="button"
              class="add-option-btn"
              @click="addPollOption"
            >
              + Add more option
            </button>
          </div>
        </section>
      </div>

      <div class="composer-modal__foot create-post-modal__foot">
        <button type="button" class="composer-btn composer-btn--ghost" @click="closeCreateModal">Cancel</button>
        <button type="button" class="composer-btn composer-btn--primary" :disabled="creatingPost" @click="handleCreatePost">
          {{ creatingPost ? 'Posting...' : 'Post' }}
        </button>
      </div>
    </div>
  </div>

  <div v-if="showPollModal" class="composer-modal-backdrop" @click.self="closePollModal">
    <div class="composer-modal poll-modal" role="dialog" aria-modal="true" aria-labelledby="pollTitle">
      <div class="composer-modal__head">
        <h2 id="pollTitle" class="composer-modal__title mb-0">Poll Results</h2>
        <button type="button" class="composer-modal__close" aria-label="Close" @click="closePollModal">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="composer-modal__body">
        <div v-if="activePollPost" class="comments-modal__post">
          <div class="comments-modal__name">{{ activePollPost.username }}</div>
          <div class="comments-modal__caption">{{ activePollPost.pollQuestion || activePollPost.pollContext || 'Poll' }}</div>
          <div v-if="activePollStatuses.length" class="poll-results__summary">
            Total votes: {{ activePollTotalVotes }}
          </div>
        </div>

        <div v-if="pollVotesLoading" class="comments-state">Loading poll results...</div>
        <div v-else-if="activePollStatuses.length" class="poll-results">
          <div
            v-for="item in activePollStatuses"
            :key="`${activePollPost.id}-${item.option}`"
            class="poll-result-row"
            :class="{ 'is-selected': item.polledStatus }"
          >
            <div class="poll-result-row__head">
              <div class="poll-result-row__option">{{ item.label }}</div>
              <div class="poll-result-row__count">{{ item.voteCount }}</div>
            </div>
            <div v-if="item.users.length" class="poll-voters-list">
              <article v-for="user in item.users" :key="`${item.option}-${user.id}`" class="poll-voter-card">
                <img
                  v-no-right-click
                  :src="user.userImage"
                  :alt="user.name"
                  class="poll-voter-card__avatar"
                />
                <div class="poll-voter-card__body">
                  <div class="poll-voter-card__name">{{ user.name }}</div>
                  <div v-if="user.email" class="poll-voter-card__meta">{{ user.email }}</div>
                  <div v-else-if="user.phone" class="poll-voter-card__meta">{{ user.phone }}</div>
                </div>
              </article>
            </div>
            <div v-else class="poll-voters-empty">No users voted for this option.</div>
          </div>
        </div>
        <div v-else class="comments-state">No poll results available.</div>
      </div>

      <div class="composer-modal__foot">
        <button type="button" class="composer-btn composer-btn--ghost" @click="closePollModal">Close</button>
      </div>
    </div>
  </div>

  <div v-if="showCommentsModal" class="composer-modal-backdrop" @click.self="closeCommentsModal">
    <div class="composer-modal comments-modal" role="dialog" aria-modal="true" aria-labelledby="commentsTitle">
      <div class="composer-modal__head">
        <h2 id="commentsTitle" class="composer-modal__title mb-0">Comments</h2>
        <button type="button" class="composer-modal__close" aria-label="Close" @click="closeCommentsModal">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="composer-modal__body comments-modal__body">
        <div v-if="activeCommentsPost" class="comments-modal__post">
          <div class="comments-modal__name">{{ activeCommentsPost.username }}</div>
          <div v-if="activeCommentsPost.title" class="comments-modal__caption">{{ activeCommentsPost.title }}</div>
        </div>

        <div v-if="commentsLoading" class="comments-state">Loading comments...</div>
        <div v-else-if="comments.length" class="comments-list">
          <article v-for="comment in comments" :key="comment.id" class="comment-card">
            <img
              v-no-right-click
              :src="comment.userImage"
              :alt="comment.username"
              class="comment-card__avatar"
            />
            <div class="comment-card__body">
              <div class="comment-card__head">
                <div class="comment-card__name">{{ comment.username }}</div>
                <div v-if="comment.createdAtLabel" class="comment-card__time">{{ comment.createdAtLabel }}</div>
              </div>
              <div class="comment-card__text">{{ comment.comment }}</div>
            </div>
          </article>
        </div>
        <div v-else class="comments-state">No comments yet.</div>

        <div class="comments-form">
          <textarea
            v-model.trim="draftComment"
            class="composer-modal__textarea comments-form__textarea"
            rows="3"
            placeholder="Add a comment..."
          ></textarea>
        </div>
      </div>

      <div class="composer-modal__foot">
        <button type="button" class="composer-btn composer-btn--ghost" @click="closeCommentsModal">Close</button>
        <button
          type="button"
          class="composer-btn composer-btn--primary"
          :disabled="commentsSubmitting || !draftComment"
          @click="submitComment"
        >
          {{ commentsSubmitting ? 'Posting...' : 'Comment' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import * as FilePond from 'vue-filepond'
import FilePondPluginImagePreview from 'filepond-plugin-image-preview'
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type'
import lightGallery from 'lightgallery'
import lgZoom from 'lightgallery/plugins/zoom'
import lgThumbnail from 'lightgallery/plugins/thumbnail'
import Swal from 'sweetalert2'
import api from '@/services/api'
import { useSessionStore } from '@/stores/session'
import BackButton from '@/components/common/BackButton.vue'
import 'filepond/dist/filepond.min.css'
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css'

const FilePondComponent = FilePond.default(FilePondPluginImagePreview, FilePondPluginFileValidateType)

const route = useRoute()
const session = useSessionStore()

const eventId = computed(() => String(route.params?.slug || route.params?.eventid || route.params?.id || '').trim())
const fallbackPath = computed(() => (eventId.value ? `/detail/${eventId.value}` : '/host'))

const loading = ref(false)
const eventTitle = ref('')
const activeFilter = ref('')
const postsRaw = ref([])
const labelsRaw = ref([])
const openMenuId = ref('')
const showCreateModal = ref(false)
const draftPostText = ref('')
const galleryHostRef = ref(null)
const busyPostId = ref('')
const showCommentsModal = ref(false)
const activeCommentsPostId = ref('')
const commentsLoading = ref(false)
const commentsSubmitting = ref(false)
const commentsRaw = ref([])
const draftComment = ref('')
const creatingPost = ref(false)
const showPollModal = ref(false)
const activePollPostId = ref('')
const pollVotesLoading = ref(false)
const busyPollPostId = ref('')
const pollVotesRaw = ref([])
const composerPond = ref(null)
const composerFiles = ref([])
const composerAudience = ref('everyone')
const composerType = ref('textPhoto')
const attachments = ref({
  textPhoto: true,
  poll: true,
  carPool: true,
  schedule: true
})
const composerForm = ref({
  scheduleTitle: '',
  scheduleStart: '',
  scheduleEnd: '',
  scheduleLocation: '',
  carPoolType: '',
  carPoolFrom: '',
  carPoolLeaving: '',
  carPoolSeats: '',
  carPoolPhone: '',
  pollQuestion: '',
  pollOptions: ['', '']
})

const fallbackAvatar = 'https://dummyimage.com/80x80/fde7ef/7a284f.png&text=U'
const composerName = computed(() => String(
  session.profile?.name ||
  session.profile?.user_name ||
  session.user?.name ||
  session.user?.user_name ||
  session.user?.username ||
  'You'
))
const composerHandle = computed(() => String(
  session.profile?.username ||
  session.user?.username ||
  ''
).trim())
const composerAvatar = computed(() => {
  const liveAvatar = String(
    session.profile?.user_img ||
    session.profile?.image ||
    session.user?.user_img ||
    session.user?.image ||
    ''
  ).trim()
  if (liveAvatar) return liveAvatar
  const initial = composerName.value.trim().charAt(0).toUpperCase() || 'U'
  return `https://dummyimage.com/80x80/fde7ef/7a284f.png&text=${encodeURIComponent(initial)}`
})

const filters = computed(() => {
  const labels = Array.isArray(labelsRaw.value) ? labelsRaw.value : []
  if (!labels.length) return [{ label: 'All', valueKey: '' }]
  return labels.map((item) => ({
    label: item?.name || 'All',
    valueKey: String(item?.value ?? '')
  }))
})

const composerAcceptedFileTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']
const composerLabelIdle = 'Drop up to 3 images or <span class="filepond--label-action">Browse</span>'
const moduleKeyMap = {
  text_photo: 'textPhoto',
  schedule: 'schedule',
  poll: 'poll',
  car_pool: 'carPool'
}
const composerTabConfig = [
  { key: 'textPhoto', label: 'Text & Photo', icon: '📸' },
  { key: 'poll', label: 'Poll', icon: '📊' },
  { key: 'carPool', label: 'Car Pool', icon: '🚗' },
  { key: 'schedule', label: 'Schedule', icon: '✍️' }
]
const availableComposerTabs = computed(() => composerTabConfig)

const buildImages = (post) => {
  const fromList = Array.isArray(post?.images) ? post.images : []
  const fromFields = [post?.image, post?.image1, post?.image2, post?.image3, post?.image4, post?.image5]
  return [...fromList, ...fromFields]
    .map(item => String(item || '').trim())
    .filter(item => item && item !== '0' && item.toLowerCase() !== 'null')
    .filter((item, index, arr) => arr.indexOf(item) === index)
}

const buildPollOptions = (post) => {
  return [post?.poll_option1, post?.poll_option2, post?.poll_option3, post?.poll_option4]
    .map(item => String(item || '').trim())
    .filter(Boolean)
}

const buildPollUsers = (value) => {
  if (!Array.isArray(value)) return []
  return value
    .map((item, index) => ({
      id: String(item?.id || index),
      name: normalizeTextValue(item?.name || item?.username || item?.user_name || 'User') || 'User',
      email: normalizeTextValue(item?.email),
      phone: normalizeTextValue(item?.phone),
      userImage: normalizeTextValue(item?.user_img || item?.userimg || item?.image) || fallbackAvatar
    }))
}

const normalizeTextValue = (value) => {
  const text = String(value || '').trim()
  if (!text || text === '0' || text.toLowerCase() === 'null') return ''
  return text
}

const formatPostDate = (value) => {
  const parsed = parseDateValue(value) || new Date()

  const now = new Date()
  const diffMs = Math.max(0, now.getTime() - parsed.getTime())
  const diffMinutes = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMinutes / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffMinutes <= 0) return 'Just now'
  if (diffMinutes === 1) return '1 min ago'
  if (diffMinutes < 60) return `${diffMinutes} mins ago`
  if (diffHours === 1) return '1 hr ago'
  if (diffHours < 24) return `${diffHours} hrs ago`
  if (diffDays === 1) return '1 day ago'
  if (diffDays < 7) return `${diffDays} days ago`

  return parsed.toLocaleDateString([], {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  }).replace(',', '')
}

const formatCommentDate = (value) => {
  const parsed = parseDateValue(value)
  if (!parsed) return ''

  const now = new Date()
  const diffMs = Math.max(0, now.getTime() - parsed.getTime())
  const diffSeconds = Math.floor(diffMs / 1000)
  const diffMinutes = Math.floor(diffSeconds / 60)
  const diffHours = Math.floor(diffMinutes / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffSeconds <= 1) return '1 sec'
  if (diffSeconds < 60) return `${diffSeconds} secs`
  if (diffMinutes === 1) return '1 min'
  if (diffMinutes < 60) return `${diffMinutes} mins`
  if (diffHours === 1) return '1 hr'
  if (diffHours < 24) return `${diffHours} hrs`
  if (diffDays === 1) return '1 day'
  return `${diffDays} days`
}

const parseDateValue = (value) => {
  const normalized = normalizeTextValue(value)
  if (!normalized) return null

  const parsed = new Date(normalized)
  if (!Number.isNaN(parsed.getTime())) return parsed

  const numeric = Number(normalized)
  if (!Number.isNaN(numeric) && numeric > 0) {
    const asMs = normalized.length <= 10 ? numeric * 1000 : numeric
    const tsDate = new Date(asMs)
    if (!Number.isNaN(tsDate.getTime())) return tsDate
  }

  return null
}

const normalizePostKind = (post) => {
  if (String(post?.poll || '0') === '1') return 'poll'
  if (String(post?.car_pool || '0') === '1') return 'car_pool'
  if (String(post?.schedule || '0') === '1') return 'schedule'
  if (String(post?.text_photo || '0') === '1') return 'text_photo'
  return 'other'
}

const buildPollStatuses = (post) => {
  const list = Array.isArray(post?.poll_options_status) ? post.poll_options_status : []
  return list
    .map((item) => ({
      option: String(item?.option || ''),
      label: normalizeTextValue(item?.poll_option),
      polledStatus: Boolean(item?.polled_status),
      voteCount: Number(item?.vote_count || 0),
      users: buildPollUsers(item?.users)
    }))
    .filter(item => item.label)
}

const normalizePostType = (post) => {
  if (String(post?.poll || '0') === '1') return '📊 Poll'
  if (String(post?.car_pool || '0') === '1') return '🚗 Car Pool'
  if (String(post?.schedule || '0') === '1') return '🗓️ Schedule'
  return ''
}

const posts = computed(() => {
  const list = Array.isArray(postsRaw.value) ? postsRaw.value : []
  return list.map((post) => ({
    id: String(post?.id || ''),
    username: String(post?.username || 'User'),
    userImage: String(post?.userimg || '').trim() || fallbackAvatar,
    hostLabel: String(post?.host_update || '').trim() ? 'Host' : 'Guest',
    kind: normalizePostKind(post),
    typeLabel: normalizePostType(post),
    title: normalizeTextValue(post?.title),
    createdAtLabel: formatPostDate(post?.created_date),
    images: buildImages(post),
    pollContext: normalizeTextValue(post?.poll_context),
    pollQuestion: normalizeTextValue(post?.poll_question),
    pollOptions: buildPollOptions(post),
    pollStatuses: buildPollStatuses(post),
    scheduleContext: normalizeTextValue(post?.schedule_context),
    scheduleDetails: normalizeTextValue(post?.schedule_details),
    scheduleFrom: normalizeTextValue(post?.schedule_fromtime),
    scheduleTo: normalizeTextValue(post?.schedule_totime),
    scheduleLocation: normalizeTextValue(post?.schedule_location),
    poolDetails: normalizeTextValue(post?.pool_details),
    poolFrom: normalizeTextValue(post?.pool_from),
    poolLeaving: normalizeTextValue(post?.pool_leaving),
    poolSeats: normalizeTextValue(post?.pool_seats),
    phoneNumber: normalizeTextValue(post?.phone_number),
    likeStatus: Number(post?.like_status || 0) === 1,
    likesCount: Number(post?.likes_count || 0),
    commentsCount: Number(post?.comments_count || 0),
    raw: post
  })).map((post) => ({
    ...post,
    scheduleTime: [post.scheduleFrom, post.scheduleTo].filter(Boolean).join(' - ')
  }))
})

const activeCommentsPost = computed(() => posts.value.find(post => post.id === activeCommentsPostId.value) || null)
const activePollPost = computed(() => posts.value.find(post => post.id === activePollPostId.value) || null)
const activePollStatuses = computed(() => {
  if (Array.isArray(pollVotesRaw.value) && pollVotesRaw.value.length) {
    return buildPollStatuses({ poll_options_status: pollVotesRaw.value })
  }
  return Array.isArray(activePollPost.value?.pollStatuses) ? activePollPost.value.pollStatuses : []
})
const activePollTotalVotes = computed(() => activePollStatuses.value.reduce((sum, item) => sum + Number(item.voteCount || 0), 0))

const comments = computed(() => {
  const list = Array.isArray(commentsRaw.value) ? commentsRaw.value : []
  return list.map((item, index) => ({
    id: String(item?.id || item?.comment_id || `${index}`),
    username: normalizeTextValue(item?.user_name || item?.username || item?.name || 'User') || 'User',
    comment: normalizeTextValue(item?.comment || item?.comments || item?.message),
    userImage: normalizeTextValue(item?.user_img || item?.userimg || item?.user_image || item?.image) || fallbackAvatar,
    createdAtLabel: formatCommentDate(item?.created_at || item?.createdAt || item?.date)
  })).filter(item => item.comment)
})

const commentsLabel = (count) => {
  const total = Number(count || 0)
  return `${total} ${total === 1 ? 'comment' : 'comments'}`
}

let galleryInstance = null

const destroyGallery = () => {
  try {
    if (galleryInstance) {
      galleryInstance.destroy(true)
      galleryInstance = null
    }
  } catch (err) {
    console.warn('LightGallery cleanup skipped:', err)
  }
}

const openPostGallery = (post, startIndex = 0) => {
  if (!galleryHostRef.value || !post?.images?.length) return

  destroyGallery()

  galleryInstance = lightGallery(galleryHostRef.value, {
    dynamic: true,
    dynamicEl: post.images.map((image) => ({
      src: image,
      thumb: image,
      subHtml: post.title ? `<p>${post.title}</p>` : ''
    })),
    plugins: [lgZoom, lgThumbnail],
    download: false
  })

  galleryInstance.openGallery(startIndex)
}

const toggleMenu = (postId) => {
  openMenuId.value = openMenuId.value === postId ? '' : postId
}

const closeMenu = () => {
  openMenuId.value = ''
}

const updatePostRaw = (postId, updater) => {
  postsRaw.value = postsRaw.value.map((item) => {
    if (String(item?.id || '') !== String(postId)) return item
    return updater({ ...item })
  })
}

const resetCreateComposer = () => {
  draftPostText.value = ''
  composerAudience.value = 'everyone'
  composerFiles.value = []
  composerForm.value = {
    scheduleTitle: '',
    scheduleStart: '',
    scheduleEnd: '',
    scheduleLocation: '',
    carPoolType: '',
    carPoolFrom: '',
    carPoolLeaving: '',
    carPoolSeats: '',
    carPoolPhone: '',
    pollQuestion: '',
    pollOptions: ['', '']
  }
  composerType.value = 'textPhoto'
  composerPond.value?.removeFiles?.()
}

const openCreateModal = () => {
  resetCreateComposer()
  showCreateModal.value = true
}

const closeCreateModal = () => {
  showCreateModal.value = false
  resetCreateComposer()
}

const closeCommentsModal = () => {
  showCommentsModal.value = false
  activeCommentsPostId.value = ''
  commentsRaw.value = []
  draftComment.value = ''
}

const closePollModal = () => {
  showPollModal.value = false
  activePollPostId.value = ''
  pollVotesRaw.value = []
}

const handleDocumentClick = () => {
  closeMenu()
}

const handleEditPost = (post) => {
  console.info('Edit post action not implemented yet:', post.id)
  closeMenu()
}

const handleDeletePost = async (post) => {
  closeMenu()
  const result = await Swal.fire({
    title: 'Delete post?',
    text: 'This post will be removed permanently.',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Delete',
    confirmButtonColor: '#d33'
  })
  if (!result.isConfirmed) return

  try {
    busyPostId.value = post.id
    const fd = new FormData()
    fd.append('post_id', post.id)
    const res = await api.deleteEventPost(fd)
    const payload = res?.data ?? res
    const ok = payload?.status === 'ok' || payload?.status === true
    if (!ok) throw new Error(payload?.msg || 'Failed to delete post')

    postsRaw.value = postsRaw.value.filter(item => String(item?.id || '') !== post.id)
    await Swal.fire({ icon: 'success', title: 'Deleted', text: payload?.msg || 'Post deleted successfully.' })
  } catch (err) {
    console.error('delete post failed', err)
    Swal.fire({ icon: 'error', title: 'Failed', text: err?.message || 'Unable to delete post' })
  } finally {
    busyPostId.value = ''
  }
}

const addPollOption = () => {
  if (composerForm.value.pollOptions.length >= 4) return
  composerForm.value.pollOptions.push('')
}

const removePollOption = (index) => {
  if (index < 2 || index >= composerForm.value.pollOptions.length) return
  composerForm.value.pollOptions.splice(index, 1)
}

const beforeAddComposerFile = (item) => {
  const file = item?.file || item
  if (file?.size > 5 * 1024 * 1024) {
    Swal.fire({ icon: 'warning', title: 'File too large', text: 'Each image must be under 5 MB.' })
    return false
  }
  if ((composerFiles.value?.length || 0) >= 3) {
    Swal.fire({ icon: 'warning', title: 'Limit reached', text: 'You can upload up to 3 images only.' })
    return false
  }
  return true
}

const handleComposerFiles = (items) => {
  composerFiles.value = items.slice(0, 3)
}

const buildCreatePostPayload = () => {
  const pollOptions = [...composerForm.value.pollOptions, '', '', ''].slice(0, 4)
  const files = composerFiles.value
    .map((item) => item?.file || item?.source || null)
    .filter((item) => item instanceof File)
    .slice(0, 3)
  const isTextPhoto = composerType.value === 'textPhoto'
  const isPoll = composerType.value === 'poll'
  const isSchedule = composerType.value === 'schedule'
  const isCarPool = composerType.value === 'carPool'

  const fd = new FormData()
  fd.append('title', isTextPhoto ? draftPostText.value : '')
  fd.append('pool_from', isCarPool ? composerForm.value.carPoolFrom : '')
  fd.append('poll', isPoll ? '1' : '0')
  fd.append('schedule_details', isSchedule ? composerForm.value.scheduleTitle : '')
  fd.append('car_pool', isCarPool ? '1' : '0')
  fd.append('text_photo', isTextPhoto ? '1' : '0')
  fd.append('poll_option4', isPoll ? pollOptions[3] : '')
  fd.append('pool_notes', isCarPool ? draftPostText.value : '')
  fd.append('poll_question', isPoll ? composerForm.value.pollQuestion : '')
  fd.append('pool_leaving', isCarPool ? composerForm.value.carPoolLeaving : '')
  fd.append('poll_option2', isPoll ? pollOptions[1] : '')
  fd.append('schedule_location', isSchedule ? composerForm.value.scheduleLocation : '')
  fd.append('user_id', String(session.userId || ''))
  fd.append('poll_option1', isPoll ? pollOptions[0] : '')
  fd.append('phone_number', isCarPool ? composerForm.value.carPoolPhone : '')
  fd.append('type', composerAudience.value || 'everyone')
  fd.append('pool_details', isCarPool ? composerForm.value.carPoolType : '')
  fd.append('schedule_context', isSchedule ? draftPostText.value || '0' : '0')
  fd.append('schedule', isSchedule ? '1' : '0')
  fd.append('event_id', eventId.value)
  fd.append('schedule_totime', isSchedule ? composerForm.value.scheduleEnd : '')
  fd.append('poll_option3', isPoll ? pollOptions[2] : '')
  fd.append('poll_context', isPoll ? draftPostText.value : '')
  fd.append('poll_type', isPoll ? 'single' : '')
  fd.append('pool_seats', isCarPool ? composerForm.value.carPoolSeats : '')
  fd.append('image1', files[0] || '')
  fd.append('image2', files[1] || '')
  fd.append('image3', files[2] || '')
  fd.append('schedule_fromtime', isSchedule ? composerForm.value.scheduleStart : '')
  return fd
}

const validateCreatePost = () => {
  if (!session.userId || !eventId.value) return 'Missing user or event context.'

  if (composerType.value === 'textPhoto') {
    if (!draftPostText.value && !composerFiles.value.length) return 'Add a caption or at least one image.'
    return ''
  }

  if (composerType.value === 'poll') {
    const options = composerForm.value.pollOptions.map((item) => item.trim()).filter(Boolean)
    if (!composerForm.value.pollQuestion) return 'Poll question is required.'
    if (options.length < 2) return 'Enter at least two poll options.'
    return ''
  }

  if (composerType.value === 'schedule') {
    if (!composerForm.value.scheduleTitle) return 'Schedule title is required.'
    return ''
  }

  if (composerType.value === 'carPool') {
    if (!composerForm.value.carPoolType) return 'Select a car pool type.'
    return ''
  }

  return ''
}

const handleCreatePost = async () => {
  const validationError = validateCreatePost()
  if (validationError) {
    Swal.fire({ icon: 'warning', title: 'Missing details', text: validationError })
    return
  }

  try {
    creatingPost.value = true
    const fd = buildCreatePostPayload()
    const res = await api.addEventPost(fd)
    const payload = res?.data ?? res
    const ok = payload?.status === 'ok' || payload?.status === true
    if (!ok) throw new Error(payload?.msg || 'Unable to create post')

    await loadEventPosts()
    closeCreateModal()
    await Swal.fire({ icon: 'success', title: 'Posted', text: payload?.msg || 'Post created successfully.' })
  } catch (err) {
    console.error('create post failed', err)
    Swal.fire({ icon: 'error', title: 'Failed', text: err?.message || 'Unable to create post' })
  } finally {
    creatingPost.value = false
  }
}

const toggleLike = async (post) => {
  if (!session.userId || !post?.id || busyPostId.value === post.id) return
  const prevLiked = post.likeStatus
  const prevCount = post.likesCount

  updatePostRaw(post.id, (item) => {
    const nextLiked = prevLiked ? 0 : 1
    const nextCount = Math.max(0, prevCount + (prevLiked ? -1 : 1))
    item.like_status = nextLiked
    item.likes_count = nextCount
    return item
  })

  try {
    busyPostId.value = post.id
    const fd = new FormData()
    fd.append('user_id', String(session.userId || ''))
    fd.append('post_id', post.id)
    const res = await api.likeEventPost(fd)
    const payload = res?.data ?? res
    const ok = payload?.status === 'ok' || payload?.status === true
    if (!ok) throw new Error(payload?.msg || 'Unable to update like')
    await loadEventPosts()
  } catch (err) {
    console.error('like post failed', err)
    updatePostRaw(post.id, (item) => {
      item.like_status = prevLiked ? 1 : 0
      item.likes_count = prevCount
      return item
    })
    Swal.fire({ icon: 'error', title: 'Failed', text: err?.message || 'Unable to update like' })
  } finally {
    busyPostId.value = ''
  }
}

const extractPollStatuses = (payload) => {
  const sources = [
    payload?.data,
    payload?.votes,
    payload?.poll_options_status,
    payload?.poll_votes,
    payload?.result
  ]

  for (const source of sources) {
    if (Array.isArray(source)) return source
    if (source && Array.isArray(source.poll_options_status)) return source.poll_options_status
    if (source && Array.isArray(source.votes)) return source.votes
    if (source && Array.isArray(source.data)) return source.data
  }

  return []
}

const isPollOptionSelected = (post, option) => {
  const statuses = String(post?.id || '') === activePollPostId.value
    ? activePollStatuses.value
    : Array.isArray(post?.pollStatuses) ? post.pollStatuses : []

  return statuses.some((item) => item.polledStatus && item.label === option)
}

const getPollOptionVoteCount = (post, option) => {
  const statuses = String(post?.id || '') === activePollPostId.value
    ? activePollStatuses.value
    : Array.isArray(post?.pollStatuses) ? post.pollStatuses : []

  return Number(statuses.find((item) => item.label === option)?.voteCount || 0)
}

const getPollOptionValue = (post, option, optionIndex) => {
  const statuses = Array.isArray(post?.pollStatuses) ? post.pollStatuses : []
  const matched = statuses.find((item) => item.label === option)
  const numericOption = Number(matched?.option || optionIndex + 1)
  return Number.isFinite(numericOption) && numericOption > 0 ? numericOption : optionIndex + 1
}

const loadPollVotes = async (postId) => {
  if (!postId) {
    pollVotesRaw.value = []
    return
  }

  pollVotesLoading.value = true
  try {
    const fd = new FormData()
    fd.append('post_id', String(postId))
    const res = await api.getEventPostVotes(fd)
    const payload = res?.data ?? res
    const ok = payload?.status === 'ok' || payload?.status === true
    pollVotesRaw.value = ok ? extractPollStatuses(payload) : []
  } catch (err) {
    console.error('load poll votes failed', err)
    pollVotesRaw.value = []
    Swal.fire({ icon: 'error', title: 'Failed', text: 'Unable to load poll results' })
  } finally {
    pollVotesLoading.value = false
  }
}

const submitPollVote = async (post, option, optionIndex) => {
  const pollOptionValue = getPollOptionValue(post, option, optionIndex)
  if (!session.userId || !post?.id || !option || !pollOptionValue || busyPollPostId.value === post.id) return

  try {
    busyPollPostId.value = post.id
    const fd = new FormData()
    fd.append('poll_options', String(pollOptionValue))
    fd.append('post_id', String(post.id))
    fd.append('user_id', String(session.userId || ''))
    const res = await api.updatePollEventPost(fd)
    const payload = res?.data ?? res
    const ok = payload?.status === 'ok' || payload?.status === true
    if (!ok) throw new Error(payload?.msg || 'Unable to update poll')

    await loadEventPosts()
    if (showPollModal.value && activePollPostId.value === String(post.id)) {
      await loadPollVotes(post.id)
    }
  } catch (err) {
    console.error('update poll failed', err)
    Swal.fire({ icon: 'error', title: 'Failed', text: err?.message || 'Unable to update poll' })
  } finally {
    busyPollPostId.value = ''
  }
}

const loadComments = async (postId) => {
  commentsLoading.value = true
  try {
    const fd = new FormData()
    fd.append('post_id', String(postId || ''))
    const res = await api.getEventPostComments(fd)
    const payload = res?.data ?? res
    const ok = payload?.status === 'ok' || payload?.status === true
    commentsRaw.value = ok
      ? (Array.isArray(payload?.data) ? payload.data : Array.isArray(payload?.comments) ? payload.comments : [])
      : []
  } catch (err) {
    console.error('load comments failed', err)
    commentsRaw.value = []
    Swal.fire({ icon: 'error', title: 'Failed', text: 'Unable to load comments' })
  } finally {
    commentsLoading.value = false
  }
}

const openComments = async (post) => {
  activeCommentsPostId.value = post.id
  showCommentsModal.value = true
  await loadComments(post.id)
}

const openPollModal = async (post) => {
  activePollPostId.value = post.id
  showPollModal.value = true
  await loadPollVotes(post.id)
}

const submitComment = async () => {
  if (!draftComment.value || !activeCommentsPostId.value || !session.userId) return

  try {
    commentsSubmitting.value = true
    const fd = new FormData()
    fd.append('post_id', activeCommentsPostId.value)
    fd.append('comment', draftComment.value)
    fd.append('parent_id', '0')
    fd.append('user_id', String(session.userId || ''))
    const res = await api.addEventPostComment(fd)
    const payload = res?.data ?? res
    const ok = payload?.status === 'ok' || payload?.status === true
    if (!ok) throw new Error(payload?.msg || 'Unable to add comment')

    await Promise.all([
      loadComments(activeCommentsPostId.value),
      loadEventPosts()
    ])
    draftComment.value = ''
  } catch (err) {
    console.error('add comment failed', err)
    Swal.fire({ icon: 'error', title: 'Failed', text: err?.message || 'Unable to add comment' })
  } finally {
    commentsSubmitting.value = false
  }
}

const loadEventDetail = async () => {
  if (!eventId.value) return
  try {
    const fd = new FormData()
    fd.append('userid', session.userId || '')
    fd.append('eventid', eventId.value)
    const res = await api.getInvitationDetail(fd)
    const payload = res?.data ?? res
    const info = payload?.eventsinfo?.[0] || {}
    eventTitle.value = info.eventname || info.orgname || ''
  } catch (e) {
    console.error('load event detail failed', e)
  }
}

const loadInviteModules = async () => {
  if (!eventId.value) return
  try {
    const fd = new FormData()
    fd.append('event_id', eventId.value)
    const res = await api.getInviteModules(fd)
    const payload = res?.data ?? res
    const list = Array.isArray(payload?.data) ? payload.data : []
    if (!list.length) return

    const next = {
      textPhoto: false,
      schedule: false,
      poll: false,
      carPool: false
    }

    list.forEach((item) => {
      const rawKey = String(item?.value || item?.id || '').trim().toLowerCase()
      const mappedKey = moduleKeyMap[rawKey]
      if (!mappedKey) return
      const status = String(item?.status ?? '').trim().toLowerCase()
      next[mappedKey] = status === '1' || status === 'true' || status === 'yes'
    })

    attachments.value = next
  } catch (e) {
    console.error('load invite modules failed', e)
  }
}

const loadEventPosts = async () => {
  if (!eventId.value || !session.userId) return
  try {
    loading.value = true
    const fd = new FormData()
    fd.append('user_id', String(session.userId || ''))
    fd.append('type_filter', activeFilter.value)
    fd.append('event_id', eventId.value)
    const res = await api.getEventPosts(fd)
    const payload = res?.data ?? res
    const ok = payload?.status === 'ok' || payload?.status === true
    labelsRaw.value = Array.isArray(payload?.labels) && payload.labels.length
      ? payload.labels
      : [{ name: 'All', value: '' }]
    postsRaw.value = ok && Array.isArray(payload?.data) ? payload.data : []
  } catch (e) {
    console.error('load event posts failed', e)
    postsRaw.value = []
    labelsRaw.value = [{ name: 'All', value: '' }]
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  await session.loadUser?.()
  await session.fetchProfile?.()
  await loadEventDetail()
  await loadInviteModules()
  await loadEventPosts()
  if (typeof document !== 'undefined') {
    document.addEventListener('click', handleDocumentClick)
  }
})

watch(activeFilter, () => {
  closeMenu()
  loadEventPosts()
})

watch(eventId, (next, prev) => {
  if (!next || next === prev) return
  activeFilter.value = ''
  closeMenu()
  loadEventDetail()
  loadInviteModules()
  loadEventPosts()
})

onBeforeUnmount(() => {
  if (typeof document !== 'undefined') {
    document.removeEventListener('click', handleDocumentClick)
  }
  destroyGallery()
})
</script>

<style scoped>
.page-bar{ position: sticky; top: 0; z-index: 1000; background: #fff; border-bottom: 1px solid rgba(0,0,0,.06); }
.page-title{ font-weight: 600; font-size: 1.05rem; }

.posts-shell{
  display:flex;
  flex-direction:column;
  gap:14px;
}

.posts-header{
  padding:2px 2px 0;
}

.posts-title{
  font-size:1.22rem;
  font-weight:700;
  color:#2a1b22;
}

.posts-subtitle{
  color:#8c6f7c;
  font-size:.92rem;
}

.composer-trigger{
  width:100%;
  border:1px solid #f2c9d8;
  background:#fff;
  border-radius:22px;
  padding:12px 14px;
  display:flex;
  align-items:center;
  gap:12px;
  box-shadow:0 10px 22px rgba(242, 172, 196, 0.12);
  text-align:left;
}

.composer-trigger__avatar{
  width:44px;
  height:44px;
  border-radius:50%;
  object-fit:cover;
  flex-shrink:0;
}

.composer-trigger__input{
  flex:1;
  border:1px solid #f3d8e2;
  border-radius:999px;
  padding:11px 14px;
  color:#9a7a88;
  font-size:.98rem;
  background:#fff;
}

.filter-strip{
  display:flex;
  gap:10px;
  overflow-x:auto;
  padding:2px 2px 6px;
}

.filter-strip::-webkit-scrollbar{
  display:none;
}

.filter-chip{
  flex:0 0 auto;
  border:1px solid #f0c8d7;
  background:#fff;
  color:#7a4a5f;
  border-radius:999px;
  padding:8px 14px;
  font-size:.9rem;
  font-weight:600;
  line-height:1;
}

.filter-chip.active{
  background:#fff4f8;
  color:#b8326d;
  box-shadow:0 6px 16px rgba(236,72,153,.14);
}

.post-list{
  display:flex;
  flex-direction:column;
  gap:14px;
}

.post-card{
  border:1px solid #f2d5df;
  border-radius:18px;
  background:linear-gradient(180deg, #fff8fb 0%, #fff2f7 100%);
  box-shadow:0 10px 24px rgba(242, 172, 196, 0.14);
  padding:14px 14px 12px;
}

.post-head{
  display:flex;
  align-items:flex-start;
  justify-content:space-between;
  gap:12px;
}

.post-user{
  display:flex;
  align-items:flex-start;
  gap:10px;
  min-width:0;
}

.post-avatar{
  width:42px;
  height:42px;
  border-radius:12px;
  object-fit:cover;
  background:#f8d7e2;
  flex-shrink:0;
}

.post-user-copy{
  min-width:0;
}

.post-username{
  font-size:1rem;
  font-weight:700;
  color:#2b1b22;
  line-height:1.2;
}

.post-meta{
  margin-top:4px;
  display:flex;
  align-items:center;
  gap:6px;
  color:#8c6f7c;
  font-size:.82rem;
  line-height:1.2;
}

.post-meta i{
  font-size:.8rem;
  color:#ec4899;
}

.post-badges{
  margin-top:6px;
  display:flex;
  flex-wrap:wrap;
  gap:8px;
}

.post-badge{
  display:inline-flex;
  align-items:center;
  border:1px solid #f0c8d7;
  background:#fff;
  color:#ec4899;
  border-radius:999px;
  padding:4px 10px;
  font-size:.8rem;
  font-weight:700;
}

.post-badge--muted{
  color:#9f6480;
}

.post-menu-wrap{
  position:relative;
  flex-shrink:0;
}

.post-menu{
  border:0;
  background:transparent;
  color:#111827;
  width:32px;
  height:32px;
  border-radius:999px;
  display:flex;
  align-items:center;
  justify-content:center;
  flex-shrink:0;
}

.post-menu-dropdown{
  position:absolute;
  top:38px;
  right:0;
  min-width:132px;
  border:1px solid #f3d4df;
  border-radius:14px;
  background:#fff;
  box-shadow:0 14px 30px rgba(86, 42, 62, 0.16);
  padding:6px;
  z-index:4;
}

.post-menu-item{
  width:100%;
  border:0;
  background:transparent;
  color:#3b2230;
  border-radius:10px;
  padding:9px 10px;
  display:flex;
  align-items:center;
  gap:10px;
  font-size:.92rem;
  font-weight:600;
  text-align:left;
}

.post-menu-item:hover{
  background:#fff4f8;
}

.post-menu-item--danger{
  color:#be123c;
}

.post-text{
  color:#2f2430;
  font-size:1rem;
  line-height:1.42;
  white-space:pre-wrap;
  word-break:break-word;
}

.post-copy{
  margin-top:12px;
  display:flex;
  flex-direction:column;
  gap:14px;
}

.post-panel{
  margin-top:12px;
  border:1px solid #f4cfdb;
  border-radius:16px;
  background:linear-gradient(180deg, #fffafb 0%, #fff1f6 100%);
  padding:14px;
}

.post-chip-row{
  display:flex;
  flex-wrap:wrap;
  gap:10px;
}

.post-panel__lead{
  color:#2f2430;
  font-size:.98rem;
  line-height:1.45;
}

.post-schedule{
  display:flex;
  flex-direction:column;
  gap:10px;
  margin-top:12px;
}

.post-detail-row{
  display:flex;
  align-items:flex-start;
  gap:10px;
  color:#57424c;
  line-height:1.4;
}

.post-detail-row i{
  margin-top:3px;
  color:#ec4899;
}

.car-pool-meta{
  margin-top:14px;
}

.post-poll-question{
  margin-top:10px;
  color:#2f2430;
  font-size:1rem;
  font-weight:600;
  line-height:1.45;
}

.post-poll-options{
  margin-top:12px;
  display:flex;
  flex-direction:column;
  gap:10px;
}

.post-poll-option{
  width:100%;
  border:1px solid #f0c8d7;
  background:#fff;
  color:#4b3140;
  border-radius:14px;
  padding:10px 12px;
  font-weight:600;
  text-align:left;
  cursor:pointer;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
}

.post-poll-option.is-selected{
  border-color:#ec4899;
  background:#fff0f6;
  color:#a61b5b;
}

.post-poll-option.is-busy{
  opacity:.7;
  cursor:progress;
}

.post-poll-option__count{
  min-width:34px;
  border-radius:999px;
  background:#fff4f8;
  color:#a61b5b;
  padding:4px 8px;
  text-align:center;
  font-size:.8rem;
  font-weight:700;
}

.post-text{
  color:#2f2430;
  font-size:1rem;
  line-height:1.42;
  white-space:pre-wrap;
  word-break:break-word;
}

.post-media-grid{
  display:grid;
  grid-template-columns:repeat(3, minmax(0, 1fr));
  gap:10px;
  justify-content:center;
  border:1px solid #f4cfdb;
  border-radius:16px;
  background:linear-gradient(180deg, #fff6fa 0%, #ffeaf3 100%);
  padding:12px;
}

.post-media-grid.count-1{
  grid-template-columns:minmax(0, 320px);
}

.post-media-grid.count-2{
  grid-template-columns:repeat(2, minmax(0, 180px));
}

.post-media-grid.count-4{
  grid-template-columns:repeat(2, minmax(0, 1fr));
}

.post-media-grid.is-centered{
  justify-content:center;
}

.post-media-tile{
  position:relative;
  overflow:hidden;
  min-height:140px;
  max-height:200px;
  border:0;
  border-radius:12px;
  background:transparent;
}

.post-media-tile.is-more::after{
  content:'';
  position:absolute;
  inset:0;
  background:rgba(43, 27, 34, 0.38);
  border-radius:12px;
}

.post-media__more{
  position:absolute;
  inset:0;
  display:flex;
  align-items:center;
  justify-content:center;
  color:#fff;
  font-size:1.1rem;
  font-weight:700;
  z-index:1;
}

.post-media__img{
  display:block;
  width:100%;
  height:100%;
  min-height:124px;
  max-height:280px;
  object-fit:cover;
  border-radius:12px;
}

.post-footer{
  margin-top:14px;
  padding-top:12px;
  border-top:1px solid #f5dbe4;
  display:flex;
  align-items:center;
  gap:28px;
}

.post-stat{
  display:inline-flex;
  align-items:center;
  gap:8px;
  color:#57424c;
  font-size:1rem;
}

.post-stat-btn{
  border:0;
  background:transparent;
  padding:0;
  cursor:pointer;
  text-decoration:none;
}

.post-stat-btn:disabled{
  opacity:.6;
  cursor:default;
}

.post-stat-link{
  color:#57424c;
}

.poll-view-btn{
  margin-left:auto;
  border:0;
  border-radius:999px;
  padding:10px 18px;
  font-weight:700;
  color:#fff;
  background:linear-gradient(90deg, #ff4d94 0%, #ff9e57 100%);
  box-shadow:0 10px 20px rgba(255, 105, 155, 0.22);
}

.post-stat__emoji{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  font-size:1rem;
  line-height:1;
}

.post-stat__emoji.is-liked{
  transform:translateY(-.5px);
}

.empty-posts{
  border:1px dashed #f2c9d8;
  border-radius:18px;
  background:#fff8fb;
  padding:20px;
  text-align:center;
}

.empty-posts__title{
  color:#402530;
  font-weight:700;
  margin-bottom:6px;
}

.empty-posts__text{
  color:#8c6f7c;
}

.composer-modal-backdrop{
  position:fixed;
  inset:0;
  background:rgba(27, 18, 24, 0.48);
  display:flex;
  align-items:center;
  justify-content:center;
  padding:16px;
  z-index:1200;
  backdrop-filter:blur(8px);
}

.composer-modal{
  width:min(560px, 100%);
  border-radius:24px;
  background:#fff;
  border:1px solid #f2d5df;
  box-shadow:0 24px 50px rgba(66, 30, 46, 0.22);
  overflow:hidden;
}

.create-post-modal{
  width:min(760px, 100%);
  max-height:min(92vh, 920px);
  display:flex;
  flex-direction:column;
  background:
    radial-gradient(circle at top right, rgba(255, 173, 201, 0.28), transparent 28%),
    linear-gradient(180deg, #fffdfd 0%, #fff7fa 100%);
}

.create-post-modal__body{
  overflow:auto;
  background:transparent;
}

.create-post-modal__identity{
  flex:1;
  min-width:0;
}

.create-post-modal__subhead{
  margin-top:4px;
  color:#8c6f7c;
  font-size:.9rem;
}

.create-post-hero{
  display:flex;
  flex-direction:column;
  gap:18px;
  padding:4px 2px 8px;
}

.create-post-chip-group{
  display:flex;
  flex-direction:column;
  gap:10px;
}

.create-post-chip-group__label{
  color:#5f4954;
  font-size:.82rem;
  font-weight:700;
  letter-spacing:.04em;
  text-transform:uppercase;
}

.create-post-modal__audience{
  display:flex;
  flex-wrap:wrap;
  gap:12px;
  margin-top:10px;
}

.audience-pill{
  display:inline-flex;
  align-items:center;
  gap:10px;
  font-weight:600;
  color:#513540;
  cursor:pointer;
  padding:10px 14px;
  border:1px solid #f1d7e1;
  border-radius:999px;
  background:rgba(255,255,255,.82);
  transition:transform .18s ease, box-shadow .18s ease, border-color .18s ease, background .18s ease;
}

.audience-pill input{
  accent-color:#f97316;
}

.audience-pill.active{
  border-color:#ec4899;
  background:#fff;
  box-shadow:0 10px 24px rgba(236,72,153,.12);
  transform:translateY(-1px);
}

.create-post-compose{
  display:grid;
  grid-template-columns:minmax(0, 1fr) minmax(280px, .9fr);
  gap:16px;
  align-items:start;
}

.create-post-section{
  display:flex;
  flex-direction:column;
  gap:14px;
  border:1px solid rgba(241, 215, 225, .95);
  border-radius:22px;
  background:rgba(255,255,255,.84);
  box-shadow:0 18px 36px rgba(124, 58, 84, 0.08);
  padding:18px;
}

.create-post-section--main{
  min-height:100%;
}

.create-post-section__title{
  color:#2b1b22;
  font-size:1rem;
  font-weight:700;
}

.create-post-fields-grid{
  display:grid;
  grid-template-columns:repeat(2, minmax(0, 1fr));
  gap:12px;
}

.composer-input{
  width:100%;
  border:1px solid #edd5df;
  border-radius:16px;
  padding:14px 18px;
  color:#2f2430;
  background:#fff;
  outline:none;
  transition:border-color .18s ease, box-shadow .18s ease, transform .18s ease;
}

.composer-input:focus{
  border-color:#ec4899;
  box-shadow:0 0 0 4px rgba(236,72,153,.08);
  transform:translateY(-1px);
}

.composer-input--full{
  grid-column:1 / -1;
}

.create-post-modal__textarea{
  background:linear-gradient(180deg, #fffdfd 0%, #fff8fb 100%);
  min-height:220px;
}

.composer-filepond{
  margin-top:4px;
}

.composer-filepond__hint{
  margin-top:8px;
  color:#8c6f7c;
  font-size:.84rem;
}

.poll-option-field{
  position:relative;
}

.poll-option-field .composer-input{
  padding-right:48px;
}

.poll-option-remove{
  position:absolute;
  top:50%;
  right:12px;
  transform:translateY(-50%);
  width:28px;
  height:28px;
  border:0;
  border-radius:999px;
  background:#fff1f6;
  color:#b8326d;
  font-size:.92rem;
  font-weight:700;
  display:flex;
  align-items:center;
  justify-content:center;
  box-shadow:0 6px 14px rgba(236,72,153,.12);
}

.add-option-btn{
  align-self:flex-start;
  border:1px solid #f0c8d7;
  border-radius:999px;
  background:#fff;
  color:#b8326d;
  font-weight:700;
  padding:10px 14px;
  text-align:left;
  box-shadow:0 10px 18px rgba(236,72,153,.08);
}

.create-post-modal__foot{
  gap:12px;
}

.create-post-tabs{
  display:flex;
  flex-wrap:wrap;
  gap:10px;
}

.create-post-tab{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  gap:8px;
  border:1px solid rgba(240, 200, 215, .95);
  background:rgba(255,255,255,.82);
  color:#7a4a5f;
  border-radius:999px;
  padding:11px 14px;
  font-weight:700;
  text-align:left;
  white-space:nowrap;
  transition:transform .18s ease, box-shadow .18s ease, border-color .18s ease, background .18s ease;
}

.create-post-tab__icon{
  font-size:1rem;
  line-height:1;
}

.create-post-tab.active{
  background:#fff;
  color:#b8326d;
  border-color:#ec4899;
  box-shadow:0 14px 28px rgba(236,72,153,.14);
  transform:translateY(-2px);
}

::v-deep(.composer-filepond .filepond--root){
  margin-bottom:0;
}

::v-deep(.composer-filepond .filepond--panel-root){
  border:1px dashed #f0c8d7;
  background:#fff;
  border-radius:18px;
}

::v-deep(.composer-filepond .filepond--drop-label){
  color:#8c6f7c;
  min-height:140px;
}

::v-deep(.composer-filepond .filepond--label-action){
  color:#ec4899;
}

.composer-modal__head,
.composer-modal__foot{
  padding:16px 18px;
  display:flex;
  align-items:center;
  justify-content:space-between;
}

.composer-modal__head{
  border-bottom:1px solid #f5dbe4;
  background:rgba(255,255,255,.76);
}

.composer-modal__body{
  padding:18px;
}

.composer-modal__title{
  color:#2b1b22;
  font-size:1.08rem;
  font-weight:700;
}

.composer-modal__close{
  border:0;
  background:#fff;
  width:34px;
  height:34px;
  border-radius:12px;
  display:flex;
  align-items:center;
  justify-content:center;
  color:#654654;
  box-shadow:0 10px 18px rgba(124, 58, 84, 0.1);
}

.composer-modal__user{
  display:flex;
  align-items:center;
  gap:12px;
  margin-bottom:14px;
}

.composer-modal__avatar{
  width:56px;
  height:56px;
  border-radius:18px;
  object-fit:cover;
  box-shadow:0 14px 28px rgba(124, 58, 84, 0.14);
}

.composer-modal__name{
  font-weight:700;
  color:#2b1b22;
  font-size:1.04rem;
}

.composer-modal__tag{
  color:#8c6f7c;
  font-size:.88rem;
  margin-top:3px;
}

.composer-modal__textarea{
  width:100%;
  border:1px solid #edd5df;
  border-radius:20px;
  padding:16px 18px;
  resize:none;
  min-height:180px;
  color:#2f2430;
  outline:none;
  transition:border-color .18s ease, box-shadow .18s ease, transform .18s ease;
}

.composer-modal__textarea:focus{
  border-color:#ec4899;
  box-shadow:0 0 0 4px rgba(236,72,153,.08);
  transform:translateY(-1px);
}

.composer-modal__foot{
  border-top:1px solid #f5dbe4;
  gap:10px;
  justify-content:flex-end;
  background:rgba(255,255,255,.82);
}

.composer-btn{
  border-radius:999px;
  padding:10px 18px;
  font-weight:700;
  border:1px solid #f1d7e1;
}

.composer-btn--ghost{
  background:#fff;
  color:#6b4c5a;
}

.composer-btn--primary{
  background:linear-gradient(90deg, #ec4899 0%, #f97316 100%);
  border-color:transparent;
  color:#fff;
  box-shadow:0 12px 24px rgba(236,72,153,.2);
}

.composer-btn:disabled{
  opacity:.6;
  cursor:default;
}

@media (max-width: 767px){
  .composer-modal-backdrop{
    padding:10px;
  }

  .create-post-modal{
    max-height:min(96vh, 920px);
    border-radius:22px;
  }

  .composer-modal__head,
  .composer-modal__body,
  .composer-modal__foot{
    padding:14px;
  }

  .create-post-compose{
    grid-template-columns:minmax(0, 1fr);
  }

  .create-post-tabs{
    gap:8px;
  }

  .create-post-fields-grid{
    grid-template-columns:minmax(0, 1fr);
  }
}

.comments-modal{
  max-width:640px;
}

.poll-modal{
  max-width:560px;
}

.comments-modal__body{
  display:flex;
  flex-direction:column;
  gap:14px;
}

.comments-modal__post{
  border:1px solid #f2d5df;
  border-radius:16px;
  background:linear-gradient(180deg, #fff8fb 0%, #fff2f7 100%);
  padding:12px 14px;
  margin-bottom: 20px;
}

.comments-modal__name{
  color:#2b1b22;
  font-weight:700;
}

.comments-modal__caption{
  color:#5f4954;
  line-height:1.45;
  margin-top:4px;
  white-space:pre-wrap;
}

.poll-results__summary{
  margin-top:10px;
  color:#8a4667;
  font-size:.86rem;
  font-weight:700;
}

.comments-list{
  display:flex;
  flex-direction:column;
  gap:12px;
  max-height:340px;
  overflow:auto;
  padding-right:4px;
}

.comment-card{
  display:flex;
  align-items:flex-start;
  gap:10px;
}

.comment-card__avatar{
  width:36px;
  height:36px;
  border-radius:50%;
  object-fit:cover;
  flex-shrink:0;
}

.comment-card__body{
  flex:1;
  border:1px solid #f2d5df;
  border-radius:16px;
  background:#fff8fb;
  padding:10px 12px;
}

.comment-card__head{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
}

.comment-card__name{
  color:#2b1b22;
  font-size:.92rem;
  font-weight:700;
}

.comment-card__time{
  color:#8c6f7c;
  font-size:.78rem;
  font-weight:600;
  flex-shrink:0;
}

.comment-card__text{
  color:#4e3743;
  line-height:1.42;
  margin-top:4px;
  white-space:pre-wrap;
}

.comments-state{
  border:1px dashed #f2c9d8;
  border-radius:16px;
  background:#fff8fb;
  color:#8c6f7c;
  padding:18px;
  text-align:center;
}

.comments-form__textarea{
  min-height:96px;
}

.poll-results{
  display:flex;
  flex-direction:column;
  gap:12px;
}

.poll-result-row{
  border:1px solid #f2d5df;
  border-radius:16px;
  background:#fff8fb;
  padding:12px 14px;
}

.poll-result-row__head{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:14px;
}

.poll-result-row__option{
  font-weight:600;
  color:#3b2230;
}

.poll-result-row__count{
  min-width:18px;
  text-align:right;
  font-size:1.08rem;
  font-weight:700;
  color:#57424c;
}

.poll-result-row.is-selected .poll-result-row__option{
  color:#8a1f51;
}

.poll-voters-list{
  margin-top:12px;
  display:flex;
  flex-direction:column;
  gap:10px;
}

.poll-voter-card{
  display:flex;
  align-items:flex-start;
  gap:10px;
  border:1px solid #f4dde5;
  border-radius:14px;
  background:#fff;
  padding:10px 12px;
}

.poll-voter-card__avatar{
  width:36px;
  height:36px;
  border-radius:50%;
  object-fit:cover;
  flex-shrink:0;
}

.poll-voter-card__body{
  min-width:0;
}

.poll-voter-card__name{
  color:#2b1b22;
  font-size:.92rem;
  font-weight:700;
}

.poll-voter-card__meta{
  margin-top:3px;
  color:#8c6f7c;
  font-size:.8rem;
  word-break:break-word;
}

.poll-voters-empty{
  margin-top:10px;
  color:#8c6f7c;
  font-size:.82rem;
}

.post-card--skeleton{
  pointer-events:none;
}

.skeleton-avatar,
.skeleton-line,
.skeleton-pill,
.skeleton-dots{
  background:linear-gradient(90deg, #fde7ef 25%, #fbd3e1 50%, #fde7ef 75%);
  background-size:200% 100%;
  animation:skeleton-shimmer 1.2s infinite linear;
}

.skeleton-avatar{
  width:42px;
  height:42px;
  border-radius:12px;
  flex-shrink:0;
}

.skeleton-line{
  height:12px;
  border-radius:999px;
}

.skeleton-line--name{
  width:120px;
  height:14px;
}

.skeleton-line--body{
  width:100%;
  margin-top:12px;
}

.skeleton-line--body.short{
  width:64%;
}

.skeleton-pill{
  width:56px;
  height:24px;
  border-radius:999px;
}

.skeleton-dots{
  width:22px;
  height:22px;
  border-radius:999px;
  margin-top:6px;
}

@keyframes skeleton-shimmer{
  from{ background-position:200% 0; }
  to{ background-position:-200% 0; }
}

@media (max-width: 576px) {
  .post-card{
    padding:12px 12px 10px;
    border-radius:16px;
  }

  .post-footer{
    gap:20px;
  }

  .post-media__img{
    max-height:320px;
  }

  .composer-trigger{
    padding:10px 12px;
  }

  .composer-trigger__input{
    font-size:.92rem;
    padding:10px 12px;
  }
}
</style>
